import { getFreshAccessToken } from './api'

const withRefreshedToken = (url: string, token: string): string =>
    url.replace(/([?&]token=)[^&]*/, `$1${token}`)

export const createAuthenticatedEventSource = (url: string): EventSource => {
    const listeners = new Map<string, Set<EventListener>>()
    let onErrorHandler: ((this: EventSource, ev: Event) => unknown) | null = null
    let retried = false
    let closed = false
    let real = new EventSource(url, { withCredentials: true })

    const handleError = (ev: Event) => {
        if (closed) return
        if (!retried) {
            retried = true
            reconnect()
            return
        }
        onErrorHandler?.call(real, ev)
    }

    const attach = (source: EventSource) => {
        listeners.forEach((names, name) => names.forEach((listener) => source.addEventListener(name, listener)))
        source.addEventListener('open', () => { retried = false })
        source.onerror = handleError
    }

    const reconnect = () => {
        real.close()
        getFreshAccessToken().then((token) => {
            if (closed) return
            real = new EventSource(withRefreshedToken(url, token), { withCredentials: true })
            attach(real)
        })
    }

    attach(real)

    return {
        addEventListener: (name: string, listener: EventListener) => {
            if (!listeners.has(name)) listeners.set(name, new Set())
            listeners.get(name)!.add(listener)
            real.addEventListener(name, listener)
        },
        removeEventListener: (name: string, listener: EventListener) => {
            listeners.get(name)?.delete(listener)
            real.removeEventListener(name, listener)
        },
        close: () => {
            closed = true
            real.close()
        },
        get onerror() { return onErrorHandler },
        set onerror(handler) { onErrorHandler = handler },
    } as unknown as EventSource
}
